import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {BASE_API} from '../config/apis';
import i18n from 'i18next';
import {APP_HEADERS, addAppHeaders} from '../config/appHeaders';

// Set by Navigation: what to do when the server says the account is inactive.
let inactiveHandler = null;
export const setInactiveUserHandler = fn => {
  inactiveHandler = fn;
};

// Create axios instance
const apiClient = axios.create({
  baseURL: BASE_API,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(addAppHeaders);

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async config => {
    try {
      // Get token from AsyncStorage
      const data = await AsyncStorage.getItem('Data');
      if (data) {
        const parseData = JSON.parse(data);
        //console.log('parseToken', parseData.token);
        if (parseData && parseData.token) {
          config.headers.Authorization = `Bearer ${parseData.token}`;
        }

        config.headers['Accept-Language'] = i18n.language === 'dt' ? 'du' : 'en';
      }
    } catch (error) {
      console.error('Error getting token from storage:', error);
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// A blocked (deactivated) account: get-profile answers isActive = 0.
// Log the user out, whichever screen asked for the profile. Registered on
// both apiClient and the global axios, because the Redux slices (getProfile,
// answerQuestions) call get-profile with plain axios.
const checkInactiveUser = response => {
  try {
    if (String(response.config?.url).includes('user/get-profile')) {
      const body = response.data;
      const isActive =
        body?.Data?.isActive ?? body?.data?.isActive ?? body?.isActive;
      if (isActive !== undefined && isActive !== null && Number(isActive) === 0) {
        inactiveHandler?.();
      }
    }
  } catch (e) {
    // never break a normal response because of this check
  }
  return response;
};
axios.interceptors.response.use(checkInactiveUser);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  checkInactiveUser,
  error => {
    // console.log, not console.error: console.error opens the red "Console
    // Error" screen in development, over a failure every caller already
    // handles (toast, error text, ...). The details still print in the log.
    console.log(
      'API Error:',
      error.response?.status,
      error.config?.method,
      error.config?.baseURL,
      error.config?.url,
      error.response?.data || error.message,
    );
    return Promise.reject(error);
  },
);

export default apiClient;

const UPLOAD_TIMEOUT_MS = 60000;

/**
 * Upload file to server (uses fetch: more reliable than axios for photos on
 * React Native).
 * @param {Object} fileData - {uri, type, name}
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 */
export const uploadFile = async fileData => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  try {
    const stored = await AsyncStorage.getItem('Data');
    const token = stored ? JSON.parse(stored)?.token : null;

    const formData = new FormData();
    formData.append('file', {
      uri: fileData.uri,
      type: fileData.type,
      name: fileData.name,
    });

    // Do NOT set Content-Type: fetch adds multipart/form-data with the
    // correct boundary by itself.
    const response = await fetch(BASE_API + 'upload-file', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...APP_HEADERS,
        'Accept-Language': i18n.language === 'dt' ? 'du' : 'en',
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
      },
      body: formData,
      signal: controller.signal,
    });

    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      // the body was not JSON
    }
    console.log('[upload-file] status', response.status, JSON.stringify(json));

    if (!response.ok) {
      return {
        success: false,
        error: json || `Upload failed (HTTP ${response.status})`,
      };
    }
    return {success: true, data: json};
  } catch (error) {
    console.error('Error uploading file:', error);
    return {
      success: false,
      error:
        error.name === 'AbortError'
          ? 'The upload took too long. Please try again.'
          : error.message || 'Upload failed',
    };
  } finally {
    clearTimeout(timer);
  }
};